// Persistent FME Identity Management
// Prevents MTU inflation by maintaining consistent user IDs across tabs and sessions

/**
 * Get or create a stable, persistent FME user ID
 * Uses localStorage for persistence across sessions and tabs
 * Includes cookie fallback for additional persistence
 * @returns {string|null} The stable FME user ID, or null during SSR
 */
export const getStableFmeId = () => {
  // SSR protection - return null during build
  if (typeof window === "undefined") return null;

  const LOCAL_STORAGE_KEY = "vwo:fme:uid";
  const COOKIE_NAME = "vwo_fme_uid";

  // Check for existing ID in localStorage
  let fmeId = localStorage.getItem(LOCAL_STORAGE_KEY);
  
  // If no ID exists, generate a new one
  if (!fmeId) {
    // Use crypto.randomUUID if available, otherwise fallback
    if (window.crypto?.randomUUID) {
      fmeId = `fme_${window.crypto.randomUUID()}`;
    } else {
      fmeId = `fme_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    }
    
    // Store in localStorage for persistence
    localStorage.setItem(LOCAL_STORAGE_KEY, fmeId);
    
    // Also set a cookie for additional persistence (14 days - optimal for A/B testing)
    try {
      const expirationDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `${COOKIE_NAME}=${fmeId}; expires=${expirationDate}; path=/; SameSite=Lax`;
    } catch (e) {
      // Cookie setting might fail in some environments
    }
    
    console.log("🎯 VWO FME: Created new persistent FME ID:", fmeId);
  }
  
  return fmeId;
};

/**
 * Read FME ID from cookie if available
 * Used as a fallback if localStorage is cleared
 * @returns {string|null} The FME ID from cookie, or null if not found
 */
export const readFmeIdFromCookie = () => {
  if (typeof document === "undefined") return null;
  
  try {
    const match = document.cookie.match(/(?:^|;\s*)vwo_fme_uid=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Restore FME ID from cookie to localStorage if needed
 * Helps maintain consistency if localStorage is cleared
 */
export const restoreFmeIdFromCookie = () => {
  if (typeof window === "undefined") return;
  
  const LOCAL_STORAGE_KEY = "vwo:fme:uid";
  
  // Check if localStorage has the ID
  const localId = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!localId) {
    // Try to restore from cookie
    const cookieId = readFmeIdFromCookie();
    if (cookieId) {
      localStorage.setItem(LOCAL_STORAGE_KEY, cookieId);
      console.log("🎯 VWO FME: Restored FME ID from cookie:", cookieId);
    }
  }
};